import { SettingFilled } from "@ant-design/icons";
import { unwrapResult } from "@reduxjs/toolkit";
import {
  Button,
  Col,
  Dropdown,
  Form,
  Input,
  InputProps,
  Pagination,
  Row,
  Switch,
  Table,
} from "antd";
import React, {
  forwardRef,
  ReactElement,
  useContext,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { useDispatch } from "react-redux";
import { RootDispatch } from "../../../../app/store";
import { GlobalKeyboardAction } from "../../../../compnents/actions/GlobalKeyboardAction";
import { Material } from "../../../../models/material";
import { FirstPage, Pager } from "../../../../models/pagination";
import { RecipeCategory } from "../../../../models/recipe";
import { Settings } from "../../../../models/setting";
import { ThemeContext } from "../../../../theme/ThemeContext";
import { sleep } from "../../../../utils/SystemUtils";
import { useTimeout } from "../../../../utils/timehooks";
import {
  getRecipeItemCompletions,
  ItemCompletionType,
} from "../../../completion/completionSlice";
import { setSettings } from "../../../settings/settingsSlice";
import {
  RecipeItemColumns,
  RecipeItemColumnsSimple,
  RecipeItemColumnsWithBatchNo,
} from "./columns";
import styles from "./RecipeItemInput.module.css";

export interface RecipeItemInputRef {
  focus: () => void;
}

interface RecipeItemInputProps {
  type: ItemCompletionType;
  category?: RecipeCategory;
  storehouseId?: string;
  filters?: boolean;
  useSpecifiedBatch?: boolean;
  ignoreStorage?: boolean;
  includeAllTcm?: boolean;
  value?: string;
  onChange?: (value: string) => void;
  onSelect?: (material: Material, ignoreStorage: boolean) => void;
  autoFocus?: boolean;
  style?: React.CSSProperties;
  disabled?:boolean
}

const RealRecipeItemInput = (
  {
    type,
    category,
    storehouseId,
    filters = true,
    value,
    useSpecifiedBatch = false,
    ignoreStorage = false,
    includeAllTcm = false,
    onChange,
    onSelect,
    onBlur,
    autoFocus = false,
    onPressEnter,
    onKeyDown,
    style,
    disabled = false,
  }: RecipeItemInputProps &
    Pick<InputProps, "onBlur" | "onPressEnter" | "onKeyDown">,
  ref: React.Ref<RecipeItemInputRef>
): ReactElement => {
  const theme = useContext(ThemeContext);

  const dispatch = useDispatch<RootDispatch>();

  const inputRef = useRef<Input>(null);

  const actionRef = useRef<GlobalKeyboardAction>(null);

  const [isVisible, setIsVisible] = useState(false);

  const [isFilterVisible, setIsFilterVisible] = useState(false);

  const [items, setItems] = useState<Material[]>([]);

  const [pager, setPager] = useState<Pager>(FirstPage);

  const [selectedRowId, setSelectedRowId] = useState("");

  const [isComposing, setIsComposing] = useState(false);

  const [params, setParams] = useState<{ keyword: string; current: number }>({
    keyword: "",
    current: 1,
  });

  const complete = (keyword: string, current: number) => {
    if (keyword) {
      let completionType = type;
      if (type === ItemCompletionType.Auto) {
        if (ignoreStorage) {
          completionType = ItemCompletionType.Material;
        } else {
          completionType = ItemCompletionType.Inventory;
        }
      }
      dispatch(
        getRecipeItemCompletions({
          type: completionType,
          keyword,
          category,
          current,
          storehouseId,
          merge: !useSpecifiedBatch,
          includeAllTcm,
        })
      )
        .then(unwrapResult)
        .then(({ items, pager }) => {
          setItems(items);
          setPager(pager);
          setIsVisible(true);
          if (items.length > 0) {
            setSelectedRowId(items[0].id);
          } else {
            setSelectedRowId("");
          }
        });
    } else {
      reset();
    }
  };

  useTimeout(
    () => {
      complete(params.keyword, params.current);
    },
    200,
    [params]
  );

  const overlay = (
    <Col>
      <Table
        className={styles.table}
        columns={
          ignoreStorage ||
          type === ItemCompletionType.Treatment ||
          type === ItemCompletionType.Material
            ? RecipeItemColumnsSimple(theme)
            : useSpecifiedBatch
            ? RecipeItemColumnsWithBatchNo(theme)
            : RecipeItemColumns(theme)
        }
        dataSource={items}
        size="small"
        pagination={false}
        rowKey={(m) => m.id}
        bordered
        onRow={(m) => ({
          onClick: () => {
            if (onSelect) {
              onSelect(m, ignoreStorage);
              reset();
            }
          },
        })}
        rowSelection={{
          type: "radio",
          selectedRowKeys: [selectedRowId],
          onChange: (ks) => setSelectedRowId(ks[0] as string),
        }}
      />
      <Pagination
        pageSize={pager.size}
        current={pager.current}
        total={pager.total}
        size="small"
        style={{
          margin: "10px 10px",
        }}
        onChange={(current) => {
          setParams({ keyword: value || "", current });
        }}
      />
    </Col>
  );

  const filter = (
    <Row>
      <GlobalKeyboardAction
        ref={actionRef}
        manually
        onEscPress={() => setIsFilterVisible(false)}
      />
      <Col
        style={{
          padding: "10px 20px",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        <Form.Item label="是否指定批次">
          <Switch
            checkedChildren="是"
            unCheckedChildren="否"
            checked={useSpecifiedBatch}
            onClick={(_, e) => e.stopPropagation()}
            onChange={(v) => {
              const [k1, k2] =
                category === RecipeCategory.Tcm ||
                category === RecipeCategory.TcmGranules
                  ? [
                      Settings.Recipe.TcmUseSpecifiedBatch,
                      Settings.Recipe.TcmIgnoreStorage,
                    ]
                  : [
                      Settings.Recipe.UseSpecifiedBatch,
                      Settings.Recipe.IgnoreStorage,
                    ];
              dispatch(
                setSettings({
                  [k1]: v,
                })
              );
              if (v) {
                dispatch(
                  setSettings({
                    [k2]: false,
                  })
                );
              }
            }}
            style={{
              backgroundColor: useSpecifiedBatch ? theme.s : theme.wsu,
            }}
          />
        </Form.Item>
        <Form.Item label="是否忽略库存">
          <Switch
            checkedChildren="是"
            unCheckedChildren="否"
            checked={ignoreStorage}
            onClick={(_, e) => e.stopPropagation()}
            onChange={(v) => {
              const [k1, k2] =
                category === RecipeCategory.Tcm ||
                category === RecipeCategory.TcmGranules
                  ? [
                      Settings.Recipe.TcmUseSpecifiedBatch,
                      Settings.Recipe.TcmIgnoreStorage,
                    ]
                  : [
                      Settings.Recipe.UseSpecifiedBatch,
                      Settings.Recipe.IgnoreStorage,
                    ];
              dispatch(
                setSettings({
                  [k2]: v,
                })
              );
              if (v) {
                dispatch(
                  setSettings({
                    [k1]: false,
                  })
                );
              }
            }}
            style={{
              backgroundColor: ignoreStorage ? theme.s : theme.wsu,
            }}
          />
        </Form.Item>
        <Button type="primary">关闭(Esc)</Button>
      </Col>
    </Row>
  );

  useEffect(() => {
    if (isFilterVisible) {
      actionRef.current?.install();
    } else {
      actionRef.current?.uninstall();
    }
  }, [isFilterVisible]);

  useEffect(() => {
    if (value) {
      setParams({ keyword: value || "", current: pager.current });
    }
  }, [useSpecifiedBatch, ignoreStorage]);

  useImperativeHandle(ref, () => ({
    focus: () => {
      inputRef.current?.focus();
    },
  }));

  const reset = () => {
    setItems([]);
    setPager(FirstPage);
    setSelectedRowId("");
    setIsVisible(false);
  };

  return (
    <Dropdown
      overlay={overlay}
      disabled={disabled}
      trigger={[]}
      visible={isVisible && items.length > 0}
      onVisibleChange={(v) => setIsVisible(v)}
    >
      <Input
        ref={inputRef}
        autoFocus={autoFocus}
        value={value}
        style={style}
        disabled={disabled}
        suffix={
          filters && type !== ItemCompletionType.Treatment ? (
            <Dropdown
              overlay={filter}
              visible={isFilterVisible}
              onVisibleChange={(v) => setIsFilterVisible(v)}
            >
              <SettingFilled />
            </Dropdown>
          ) : undefined
        }
        onFocus={() => setIsVisible(true)}
        onBlur={async (e) => {
          onBlur && onBlur(e);
          await sleep(200);
          setIsVisible(false);
        }}
        onCompositionStart={() => {
          setIsComposing(true);
        }}
        onCompositionEnd={(e) => {
          setIsComposing(false);
          setParams({ keyword: e.data, current: 1 });
        }}
        onChange={(e) => {
          if (!isComposing) {
            setParams({ keyword: e.target.value, current: 1 });
          }
          if (onChange) {
            onChange(e.target.value);
          }
        }}
        onKeyDown={(event) => {
          onKeyDown && onKeyDown(event);
          if (!selectedRowId) {
            return;
          }
          let index = items.findIndex((m) => m.id === selectedRowId);
          switch (event.key) {
            case "ArrowDown":
              index = index + 1;
              break;
            case "ArrowUp":
              index = index - 1;
              break;
            case "ArrowRight":
              if (pager.current * pager.size < pager.total) {
                setParams({ keyword: value || "", current: pager.current + 1 });
              }
              break;
            case "ArrowLeft":
              if (pager.current > 1) {
                setParams({ keyword: value || "", current: pager.current - 1 });
              }
              break;
          }
          if (index >= 0 && index < items.length) {
            setSelectedRowId(items[index].id);
          }
        }}
        onPressEnter={(e) => {
          onPressEnter && onPressEnter(e);
          if (!selectedRowId || !onSelect) {
            return;
          }
          const item = items.find((m) => m.id === selectedRowId);
          if (item) {
            onSelect(item, ignoreStorage);
            reset();
          }
        }}
      />
    </Dropdown>
  );
};

export const RecipeItemInput = forwardRef(RealRecipeItemInput);
